import { HasID } from '@akin-home/records';
import { Recurrable, getCustomRecurrenceData } from '@akin-home/recurrence';

interface MinimalChecklist {
  id: string;
  title: string;
  checked: boolean;
}

// Adds checklist data to a recurrence item
export const addRecurrenceChecklistData =
  <T>(origin: Recurrable<T> & { checklist: MinimalChecklist[] }) =>
  (recurrenceItem: T & HasID): T => ({
    ...recurrenceItem,

    // Get the checklist data from the original item + custom recurrence data
    checklist: reconstructRecurrenceChecklist(
      origin.checklist,
      getCustomRecurrenceData(origin)[recurrenceItem.id]?.checklist
    ),
  });

// Processes recurrence checklists
export const processRecurrenceChecklist = <T extends MinimalChecklist>(
  item: T,
  overrideData: boolean | Partial<MinimalChecklist>
): T => {
  let itemOverride = {
    checked: false, // Whenever we create recurrence, we want to default all checked items to false
  } as MinimalChecklist;

  if (typeof overrideData === 'boolean') {
    // Maintains backwards compataibility where overrideData used to just contained `checked` value
    itemOverride.checked = overrideData || false;
  } else if (overrideData !== undefined) {
    itemOverride = { ...itemOverride, ...overrideData };
  }

  return {
    ...item,
    ...itemOverride,
    id: item.id, // Prevent ID from accidently being overriden
  };
};

// Construct a checklist instance from origin checklist data and recurrence override data
export const reconstructRecurrenceChecklist = (
  originChecklist: MinimalChecklist[] | undefined,
  overrideData:
    | {
        [key: string]:
          | boolean
          | (Partial<MinimalChecklist> & { deleted?: boolean });
      }
    | undefined
) => {
  const newChecklist: MinimalChecklist[] = [];
  const processedItemIds: { [k: string]: boolean } = {};

  originChecklist?.forEach((item) => {
    const itemOverrideData = overrideData?.[item.id];
    processedItemIds[item.id] = true;

    if (typeof itemOverrideData === 'object' && itemOverrideData?.deleted) {
      // If deleted, then do not add to newChecklist
      return;
    } else if (itemOverrideData !== undefined) {
      newChecklist.push(processRecurrenceChecklist(item, itemOverrideData));
    } else {
      newChecklist.push(item);
    }
  });

  // Add new items of this recurrence instance
  const newItems = [];
  for (const itemId in overrideData) {
    if (processedItemIds[itemId]) {
      continue;
    }
    newItems.push({
      // Default values
      checked: false,
      title: 'Untitled',

      ...(overrideData[itemId] as Partial<MinimalChecklist> & {
        index?: number;
      }),
      id: itemId,
    });
  }
  newItems.sort((item1, item2) => (item1.index ?? -1) - (item2.index ?? -1));

  return newChecklist.concat(newItems);
};

// Make a changelog between a recurrence checklist instance and the origin checklist
export const makeRecurrenceChecklistChangelog = (
  recurrenceChecklist: MinimalChecklist[],
  originChecklist: MinimalChecklist[]
) => {
  const originChecklistIds: any[] =
    originChecklist?.map((item) => item.id) ?? [];

  const deletedIds = originChecklistIds.filter(
    (id) => recurrenceChecklist.findIndex((item) => item.id === id) < 0
  );

  const deletedItems = deletedIds.reduce(
    (acc, id) => ({ ...acc, [id]: { deleted: true } }),
    {}
  );

  return recurrenceChecklist.reduce(
    (acc, { id, ...rest }, index) => ({ ...acc, [id]: { ...rest, index } }),
    deletedItems ?? {}
  );
};
